<template>
  <div class="footer">
    <!-- Bottom Menu -->
    <div v-if="input_type === 'menu'" class="footer-menu d-flex" :class="input_type === 'menu' ? 'footer-menu-appear' : 'footer-menu-dismiss'" align="center">
      <div class="menu-item" v-for="(button, i) in bottom_menu.buttons" :key="i">
        <!-- Image-based menu -->
        <v-img v-if="button.image_url" :alt="button.title" :src="button.image_url" class="menu-item-image-btn" @click="selectMenuOption(button)">
          <template v-slot:placeholder>
            <v-skeleton-loader type="image" />
          </template>
        </v-img>

        <!-- Text-based menu -->
        <v-btn v-else-if="button.title" class="menu-item-text-btn text-capitalize" :style="button.style" variant="text" dark @click="selectMenuOption(button)">
          <v-icon>{{ button.icon }}</v-icon>
          {{ button.title }}
        </v-btn>
      </div>
    </div>

    <!-- footer bar -->
    <div class="footer-bar d-flex" align="center" :style="input_type === 'menu' ? '' : 'border-top: 1px solid rgba(0, 0, 0, 0.2)'">
      <!-- input type toggle btn -->
      <div class="menu-btn">
        <v-btn class="icon-btn" icon @click="toggleInputType('left')" plain variant="text" :ripple="false" aria-label="inputbar-menu-btn">
          <v-icon v-if="input_type === 'text'">mdi-menu</v-icon>
          <v-icon v-else>mdi-keyboard</v-icon>
        </v-btn>
      </div>

      <!-- Menu Type -->
      <div v-if="input_type === 'menu'" style="flex-grow: 100"></div>

      <!-- Recording Type -->
      <div v-else-if="input_type === 'record'" style="flex-grow: 100">
        <span v-if="microphone_loading" class="loading-record-btn record-btn fade-in">
          <v-progress-circular color="#e22925" indeterminate style="height: 1.25rem; width: 1.25rem"></v-progress-circular>
        </span>
        <span
          v-else
          class="record-btn fade-in"
          :class="recording ? 'recording-btn' : 'hold-to-record-btn'"
          @mousedown="startRecording"
          @mouseup="endRecording"
          @touchstart="startRecording"
          @touchend="endRecording"
        >
          <v-icon :class="{ blinking: recording }" size="20" :color="recording ? '#e22925' : '#ffffff'" style="margin: 0 0.25rem 0.1rem 0">mdi-microphone</v-icon>
          <span v-if="recording" style="color: #e22925">{{ $vuetify.locale.t('$vuetify.__InputBar.recording') }}</span>
          <span v-else style="color: #ffffff">{{ $vuetify.locale.t('$vuetify.__InputBar.press_to_record') }}</span>
        </span>
      </div>

      <!-- Text Type -->
      <v-row v-else-if="input_type === 'text'" no-gutters align="center" style="flex-grow: 100">
        <v-text-field
          v-model="input"
          :disabled="input_bar.disabled"
          :placeholder="input_placeholder"
          density="compact"
          single-line
          variant="plain"
          hide-details
          class="fade-in pb-1"
          style="background-color: transparent !important"
          @keyup.enter="sendInput"
          @focus="handleInputFocus"
          autofocus
        />

        <div class="input-function">
          <div v-show="input.length > 0">
            <v-btn variant="text" class="icon-btn" icon @click="sendInput"><v-icon :color="config.main_color">mdi-send</v-icon></v-btn>
          </div>

          <div v-show="input.length === 0 && input_bar.input_bar_mode === 'full'">
            <v-btn variant="text" class="icon-btn" icon style="margin-right: 0.5rem" aria-label="inputbar-attach-btn"><v-icon>mdi-attachment</v-icon></v-btn>
            <v-btn variant="text" class="icon-btn" icon @click="toggleInputType('right')" aria-label="inputbar-mic-btn"><v-icon :color="config.main_color">mdi-microphone</v-icon></v-btn>
          </div>
        </div>
      </v-row>
    </div>
  </div>
</template>

<script setup>
// Import modules
import global_config from '@/../config.json'
import moment from 'moment'
import recorder_core from 'recorder-core'
import 'recorder-core/src/engine/mp3'
import 'recorder-core/src/engine/mp3-engine'
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useLocale } from 'vuetify'

// Init modules
const store = useStore()
const { t } = useLocale()

// Data
const config = ref(global_config)
const input = ref('')
const recorder = ref(null)
// const is_safari = ref(false)
// const is_firefox = ref(false)
const microphone_loading = ref(false)
const recording = ref(false)

// Computed
const input_type = computed({
  get() {
    return store.state.chat.input_type
  },
  set(value) {
    store.commit('SET_CHAT_INPUT_TYPE', value)
  }
})

const input_bar = computed(() => {
  return store.state.chat.input_bar
})

const input_placeholder = computed(() => {
  const placeholder = input_bar.value?.placeholder || null
  if (placeholder === '' || placeholder === null) return t('$vuetify.__InputBar.placeholder')
  else return input_bar.value.placeholder
})
const bottom_menu = computed(() => {
  return store.state.chat.bottom_menu
})

const menu_visibility = computed(() => {
  return bottom_menu.value.visibility
})

// Methods
const toggleInputType = (from) => {
  if (from === 'right') input_type.value = 'record'
  else {
    if (input_type.value === 'text' && bottom_menu.value.buttons.length) input_type.value = 'menu'
    else input_type.value = 'text'
  }
}

const promptPermission = () => {
  microphone_loading.value = true
  recorder.value = recorder_core({ type: 'mp3', sampleRate: 16000, bitRate: 16 })

  recorder.value.open(() => {
    microphone_loading.value = false
  })
}
const startRecording = () => {
  recording.value = true
  recorder.value.start()
}
const endRecording = () => {
  recording.value = false

  recorder.value.stop((blob) => {
    let audio_url = (window.URL || window.webkitURL).createObjectURL(blob)
    const datetime = moment().format('hh:mm A')
    store.commit('APPEND_CHAT_HISTORY', { type: 'user', datetime, messages: [{ type: 'audio', audio_url: audio_url }] })
    store.dispatch('sendAudioToSocket', blob)
  })
}

const selectMenuOption = ({ type, title, value }) => {
  if (type === 'web_url') window.open(value, '_blank')
  else store.dispatch('sendMessageToSocket', { title, value })
  toggleInputType('left')
}

const sendInput = () => {
  if (input.value !== '') store.dispatch('sendMessageToSocket', { title: input.value, value: input.value })
  input.value = ''
}

const handleInputFocus = () => {
  // Prevent unwanted scrolling when the user clicks on the input field
  // This helps prevent the chat container from scrolling to the top
  // when the user focuses on the text input
  setTimeout(() => {
    const chatContainer = document.getElementById('chat_container')
    if (chatContainer) {
      // Maintain the current scroll position
      const currentScrollTop = chatContainer.scrollTop
      chatContainer.scrollTop = currentScrollTop
    }
  }, 0)
}

// Watch
watch(input_type, (type) => {
  if (type === 'record') promptPermission()
})

watch(menu_visibility, (value) => {
  if (value) input_type.value = 'menu'
})
</script>

<style scope>
.footer {
  flex-direction: column;
  z-index: 100;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  /* -webkit-animation: slide-in 0.3s forwards;
  animation: slide-in 0.3s forwards;
  transform: translateY(500px); */
}

/* footer menu */
.footer-menu {
  border-top: 1px solid rgba(0, 0, 0, 0.2);
  min-height: 4.5rem;
  padding: 0.75rem 0rem 0.75rem 0;
  background-color: #f0f0f0;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;

  position: relative;
  z-index: 200;
}

.footer-menu-appear {
  -webkit-animation: slide-in 0.3s forwards;
  animation: slide-in 0.3s forwards;
  transform: translateY(500px);
}

.footer-menu-dismiss {
  -webkit-animation: slide-out 0.3s forwards;
  animation: slide-out 0.3s forwards;
}

@-webkit-keyframes slide-in {
  100% {
    transform: translateY(0);
  }
}

@keyframes slide-in {
  100% {
    transform: translateY(0);
  }
}

@-webkit-keyframes slide-out {
  100% {
    transform: translateY(35rem);
  }
}

@keyframes slide-out {
  100% {
    transform: translateY(35rem);
  }
}

.menu-item {
  margin: 0.5rem;
}

.menu-item-image-btn {
  border-radius: 0.375rem;
  height: 4.375rem;
  width: 4.375rem;
}

.menu-item-text-btn {
  box-shadow: 0 0.25rem 0.3125rem rgba(0, 0, 0, 0.14), 0 0.0625rem 0.625rem rgba(0, 0, 0, 0.12), 0 0.125rem 0.25rem rgba(0, 0, 0, 0.2);
  border-radius: 0.5rem;
  font-weight: 700;
  background-color: #828282 !important;
}

/* record */
.loading-record-btn {
  display: block;
  width: 6rem;
  background: #f0f0f0;
}

.record-btn {
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem 0.5rem 0.5rem;
  margin-left: -2.5rem;
  font-weight: 700;
  font-size: 0.875rem;
  line-height: 1.25rem;

  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
}

.recording-btn {
  background: rgba(226, 41, 37, 0.08);
}

.hold-to-record-btn {
  background: #e22925;
  box-shadow: 0 0.25rem 0.3125rem rgba(0, 0, 0, 0.14), 0 0.0625rem 0.625rem rgba(0, 0, 0, 0.12), 0 0.125rem 0.25rem rgba(0, 0, 0, 0.2) !important;
}

.blinking {
  -webkit-animation: 1.25s blink ease infinite;
}

@keyframes blink {
  from,
  to {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
}

/* input menu */
.footer-bar {
  height: 3.5rem;
  background-color: #ffffff;
  background: #ffffff;
  align-items: center;
  position: relative;
  z-index: 3000;
}

.menu-btn {
  flex-grow: 1;
  padding-left: 0.75rem;
}

.input-function {
  padding-right: 0.75rem;
}

.v-btn.icon-btn {
  height: 1.5rem !important;
  width: 1.5rem !important;
}

.v-btn.icon-btn > .v-btn__content > .v-icon {
  font-size: 1.5rem !important;
}

/* text field */
.v-text-field input {
  font-weight: 400;
  font-size: 0.9375rem;
  line-height: 1.125rem;
  letter-spacing: 0.1px;
  color: #828282;
}

.v-text-field.v-text-field--solo:not(.v-text-field--solo-flat) > .v-input__control > .v-input__slot {
  box-shadow: none !important;
}

.fade-in {
  animation: fade-in 0.25s;
}

input[type='text'],
select:focus,
textarea {
  font-size: 16px;
}

@keyframes fade-in {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
</style>
